'use client';

import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { heroContent } from '../config';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const subtextRef = useRef<HTMLParagraphElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const shapesRef = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      // Animate headline lines
      const lines = headlineRef.current?.querySelectorAll('.headline-line');
      lines?.forEach((line, index) => {
        const span = line.querySelector('span');
        if (span) {
          gsap.set(span, { yPercent: 100 });
          tl.to(
            span,
            {
              yPercent: 0,
              duration: 1,
              ease: 'power3.out',
            },
            index * 0.15,
          );
        }
      });

      // Animate subtext
      tl.to(
        subtextRef.current,
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
        },
        '-=0.3',
      );

      //   Animate buttons
      if (buttonsRef.current?.children) {
        tl.fromTo(
          Array.from(buttonsRef.current.children),
          { opacity: 0, y: 20, scale: 0.95 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.6,
            stagger: 0.1,
            ease: 'power2.out',
          },
          '-=0.4',
        );
      }

      // Animate floating shapes
      shapesRef.current.forEach((shape, index) => {
        if (shape) {
          gsap.to(shape, {
            opacity: 1,
            duration: 1,
            delay: 0.5 + index * 0.1,
          });

          gsap.to(shape, {
            x: 'random(-30, 30)',
            y: 'random(-30, 30)',
            rotation: 'random(-5, 5)',
            duration: 'random(4, 6)',
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
            delay: index * 0.2,
          });
        }
      });

      // Parallax effect on scroll
      if (window.innerWidth > 768) {
        gsap.to('.floating-shape', {
          yPercent: -15,
          ease: 'none',
          scrollTrigger: {
            trigger: heroRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: 1,
          },
        });
      }
    }, heroRef);

    return () => ctx.revert();
  }, []);

  const addToShapesRef = (el: HTMLDivElement | null) => {
    if (el && !shapesRef.current.includes(el)) {
      shapesRef.current.push(el);
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-[600px] flex items-center justify-center bg-neutral-off-white p-0"
      style={{ perspective: '2000px' }}
    >
      <div className="w-full max-w-[92vw] xl:max-w-[1400px] mx-auto px-0">
        <div className="relative rounded-[2.5rem] bg-primary-navy shadow-2xl px-4 md:px-8 lg:px-12 py-12 md:py-16 lg:py-20 flex items-center min-h-[500px] lg:min-h-[600px] overflow-visible">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center w-full relative z-10">
            {/* Left Content */}
            <div className="space-y-6 md:space-y-8 pt-4 lg:pt-0">
              {/* Headline Reveal */}
              <div
                className="overflow-hidden"
                style={{ perspective: '1000px' }}
              >
                <motion.div
                  ref={headlineRef}
                  className="space-y-2"
                  initial={{
                    rotateX: 90,
                    opacity: 0,
                    transformOrigin: 'bottom',
                  }}
                  animate={{ rotateX: 0, opacity: 1 }}
                  transition={{
                    duration: 1.2,
                    ease: [0.33, 1, 0.68, 1],
                    delay: 1.0,
                  }}
                >
                  <div className="headline-line">
                    <span className="block text-[2rem] md:text-[2.5rem] lg:text-[3rem] xl:text-[3.5rem] font-bold leading-tight text-white">
                      {heroContent.headline.line1}
                      <br />
                      {heroContent.headline.line2}
                      <br />
                      {heroContent.headline.line3}
                    </span>
                  </div>
                </motion.div>
              </div>
              {/* Subtext Masked Slide */}
              <div
                className="overflow-hidden"
                style={{ perspective: '1000px' }}
              >
                <motion.p
                  ref={subtextRef}
                  className="text-base md:text-lg lg:text-xl text-white/80 max-w-2xl leading-relaxed"
                  initial={{
                    rotateX: 90,
                    opacity: 0,
                    transformOrigin: 'bottom',
                  }}
                  animate={{ rotateX: 0, opacity: 1 }}
                  transition={{
                    duration: 1,
                    ease: [0.33, 1, 0.68, 1],
                    delay: 2.5,
                  }}
                >
                  {heroContent.subtext}
                </motion.p>
              </div>
              {/* Button Reveal */}
              <div
                className="overflow-hidden"
                style={{ perspective: '1000px' }}
              >
                <motion.div
                  ref={buttonsRef}
                  className="flex flex-wrap gap-4"
                  initial={{
                    rotateX: 90,
                    opacity: 0,
                    transformOrigin: 'bottom',
                  }}
                  animate={{ rotateX: 0, opacity: 1 }}
                  transition={{
                    duration: 0.9,
                    ease: [0.33, 1, 0.68, 1],
                    delay: 2.8,
                  }}
                >
                  <motion.a
                    href={heroContent.buttons.primary.href}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-8 py-4 bg-accent-blue text-white font-semibold rounded-lg shadow-glow-blue hover:bg-accent-cyan transition-all duration-300"
                  >
                    {heroContent.buttons.primary.text}
                  </motion.a>
                  <motion.a
                    href={heroContent.buttons.secondary.href}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-8 py-4 bg-white text-primary-navy font-semibold rounded-lg shadow-glow-blue hover:bg-accent-blue hover:text-white transition-all duration-300 border border-accent-blue"
                  >
                    {heroContent.buttons.secondary.text}
                  </motion.a>
                </motion.div>
              </div>
              {/* Ratings Reveal */}
              {/* <div
                className="overflow-hidden"
                style={{ perspective: '1000px' }}
              >
                <motion.div
                  className="flex flex-wrap gap-4 md:gap-8 pt-4"
                  initial={{
                    rotateX: 90,
                    opacity: 0,
                    transformOrigin: 'bottom',
                  }}
                  animate={{ rotateX: 0, opacity: 1 }}
                  transition={{
                    duration: 0.9,
                    ease: [0.33, 1, 0.68, 1],
                    delay: 3.1,
                  }}
                >
                  <div className="flex items-center gap-2">
                    <img
                      src="https://www.gstatic.com/images/branding/product/1x/googleg_32dp.webp"
                      alt="Google"
                      className="w-5 h-5 md:w-6 md:h-6"
                    />
                    <span className="text-white font-semibold text-base md:text-lg">
                      4.8
                    </span>
                    <span className="text-white/70 text-xs md:text-sm">
                      From 799+ reviews
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center bg-green-500 text-white rounded px-2 py-1 text-xs font-bold">
                      &#9733;&#9733;&#9733;&#9733;&#9733;
                    </span>
                    <span className="text-white font-semibold text-base md:text-lg">
                      4.9
                    </span>
                    <span className="text-white/70 text-xs md:text-sm">
                      From 200+ reviews
                    </span>
                  </div>
                </motion.div>
              </div> */}
            </div>
            {/* Right Image with 3D Upward Reveal and Background Shapes */}
            {/* Absolutely positioned hero image for better centering and visibility */}
            <div className="relative h-[400px] lg:h-[600px] w-full flex items-end justify-center">
              {/* Background shape-1 */}
              <img
                src="/images/shape-1.webp"
                alt="Shape 1"
                className="absolute left-0 bottom-0 w-40 lg:w-64 opacity-60 pointer-events-none select-none z-0"
                draggable="false"
                style={{ filter: 'blur(1px)' }}
              />
              {/* Background world image */}
              <img
                src="/images/world.webp"
                alt="World Map"
                className="absolute right-0 top-1/2 -translate-y-1/2 w-48 lg:w-80 opacity-30 pointer-events-none select-none z-0"
                draggable="false"
                style={{ filter: 'blur(0.5px)' }}
              />
              {/* Hero Image positioned at bottom and fully visible */}
              <motion.img
                src="/images/hero-1.webp"
                alt="Hero"
                className="object-contain h-[110%] w-auto drop-shadow-2xl select-none pointer-events-none relative z-10"
                draggable="false"
                initial={{
                  rotateX: 90,
                  opacity: 0,
                  y: 100,
                  transformOrigin: 'bottom',
                }}
                animate={{ rotateX: 0, opacity: 1, y: 0 }}
                transition={{
                  duration: 1.2,
                  ease: [0.33, 1, 0.68, 1],
                  delay: 1.5,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
